import * as turf from "@turf/turf";
import { nowUtc } from "./appNow";
import { zoneValueLabel } from "./zoneLabels.js";
import {
  ALGORITHM_PATHFINDER,
  ALGORITHM_PLANROUTE_ANALYSER,
} from "./coreRequestBuilder";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import localizedFormat from "dayjs/plugin/localizedFormat";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";

dayjs.extend(utc);
dayjs.extend(localizedFormat);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const zoomWpNumberThreshold = {
  0: 200,
  1: 200,
  2: 200,
  3: 100,
  4: 100,
  5: 50,
  6: 50,
  7: 10,
  8: 10,
  9: 1,
  10: 1,
  11: 1,
  12: 1,
  13: 1,
  14: 1,
  15: 1,
  16: 1,
  17: 1,
  18: 1,
  19: 1,
};

// Merge point + segment zone warnings (parallel name/detail arrays from astar), deduplicated.
function zoneEntries(pointZone, pointDetail, segmentZones, segmentDetails) {
  const seen = new Set();
  const entries = [];
  const add = (name, detail) => {
    if (!name || seen.has(name)) return;
    seen.add(name);
    entries.push({ name, detail: zoneValueLabel(detail) });
  };
  add(pointZone, pointDetail);
  if (Array.isArray(segmentZones)) {
    segmentZones.forEach((zone, i) => add(zone, segmentDetails?.[i]));
  }
  return entries;
}

// Zones at a waypoint (point + outgoing segment) as { kind: high_risk|war_risk|operational_risk|eca, name, detail }.
export function waypointZones(warnings) {
  if (!warnings || typeof warnings !== "object") return [];
  const w = warnings;
  return [
    ["high_risk", zoneEntries(w.warning_high_risk_area, w.warning_high_risk_area_risk_type, w.warning_segment_high_risk_area, w.warning_segment_high_risk_area_risk_type)],
    ["war_risk", zoneEntries(w.warning_jwla, w.warning_jwla_peril, w.warning_segment_jwla, w.warning_segment_jwla_peril)],
    ["operational_risk", zoneEntries(w.warning_custom_area, undefined, w.warning_segment_custom_area)],
    ["eca", zoneEntries(w.warning_eca, w.warning_eca_regime, w.warning_segment_eca, w.warning_segment_eca_regime)],
  ].flatMap(([kind, entries]) => entries.map((e) => ({ kind, ...e })));
}

// Weather and zone runs are drawn from route_summary transits (features/route/transits.js).
export function hasLegWarnings(properties) {
  const w = properties?.warnings;
  if (!w || typeof w !== "object") return false;
  return (
    w.warning_is_closed_viaarea === true ||
    w.warning_leg_usable === true ||
    w.warning_is_free_usage === true ||
    w.warning_vertical_clearance === true ||
    w.warning_water_depth === true
  );
}

export function hasLegInfos(properties, { includeUncheckedLeg = true } = {}) {
  const rnw = properties?.rnw_evaluation ?? properties?.rnwEvaluation;
  if (rnw?.ww_maxspeed_knots != null || rnw?.ww_minspeed_knots != null)
    return true;
  return includeUncheckedLeg && !!uncheckedLegInfo(properties?.warnings);
}

// Condition of the matching RNW rule arm; the fallback "Otherwise" arm says nothing beyond the warning.
export function restrictionRuleText(restrictions) {
  const [condition] = restrictions?.ww_restriction_text_check?.trim().split(/\s*=>\s*/) ?? [];
  if (!condition || /^otherwise$/i.test(condition)) return null;
  return condition.charAt(0).toUpperCase() + condition.slice(1);
}

// astar flags the outgoing leg when it joins an exact start/end position
// (or a reported position) without checking land/port areas.
export function uncheckedLegInfo(warnings) {
  return warnings?.warning_segment_controlled_area_unchecked === true
    ? "Leg not checked for land or port areas"
    : null;
}

export function waypointLabel(properties) {
  const ext = properties?.extended_number;
  return `WP ${properties?.number ?? ""}${ext ? `.${ext}` : ""}`;
}

/**
 * True if a route type is a planned route. Matches the ShippingDB route type
 * 'planned'.
 * @param {string} routeType
 * @returns {boolean}
 */
export function isPlanType(routeType) {
  return routeType === "planned";
}

// Key order drives the PlanStep optimisation pill (Time | Fuel | Manual).
export const OPTIMISED_FOR_LABELS = {
  time: "Time",
  fuel: "Fuel",
  manual: "Manual",
};

// Core encodes fuel optimisation into the astar request as
// weights.consumption 1.0 (see apply_fuel_features_to_astar).
export function isFuelOptimisedRequest(jsonRequest) {
  return (jsonRequest?.weights?.consumption ?? 0) > 0;
}

/**
 * Optimization target encoded in an astar request: analyser = user-set
 * waypoints (manual), pathfinder = time unless fuel-weighted; null for
 * anything else (unknown).
 */
export function optimisedForFromRequest(jsonRequest) {
  if (jsonRequest?.algorithm === ALGORITHM_PLANROUTE_ANALYSER) return "manual";
  if (jsonRequest?.algorithm !== ALGORITHM_PATHFINDER) return null;
  return isFuelOptimisedRequest(jsonRequest) ? "fuel" : "time";
}

/**
 * Optimization target of a route: "time" | "fuel" | "manual", or null when
 * unknown (imports, reported routes). Prefers the explicit column — a
 * present-but-unrecognised value stays null rather than guessing from the
 * request; only legacy routes (column NULL) fall back to the persisted
 * astar request.
 */
export function findOptimisedFor(route) {
  const explicit = route?.optimisedFor;
  if (explicit != null) {
    return Object.hasOwn(OPTIMISED_FOR_LABELS, explicit) ? explicit : null;
  }
  return optimisedForFromRequest(route?.jsonRequest);
}

// Line/point color per planned-route lifecycle state (warnings/info override).
export const PLANNED_STATE_COLORS = {
  active: "#4caf50",
  suggested: "#7b1fa2",
  archived: "#6b7480",
};

export const PATHFINDER_COLOR = "#d4a106";
export const REPORTED_ROUTE_COLOR = "#0000ff";

// Generic fallback accent for non-route cards (matches the old card default).
const CARD_DEFAULT_COLOR = "#004f7d";

/**
 * Resolve the accent color for a vessel/waypoint MapInfoCard so the card header
 * and icon match the route's color on the map. Mirrors the map styling:
 * planned routes follow their lifecycle state (active/suggested/archived), the
 * unsaved Pathfinder candidate is amber, reported routes blue.
 * @param {string} routeType
 * @param {"active"|"suggested"|"archived"|string|null|undefined} state
 * @returns {string} hex color
 */
export function findCardColor(routeType, state) {
  if (routeType === "Pathfinder") return PATHFINDER_COLOR;
  if (isPlanType(routeType)) return findPlannedStateColor(state);
  if (routeType === "reported") return REPORTED_ROUTE_COLOR;
  return CARD_DEFAULT_COLOR;
}

/**
 * Map a planned route's lifecycle state to its line/point color. Unknown or
 * missing state falls back to active green.
 * @param {"active"|"suggested"|"archived"|string|null|undefined} state
 * @returns {string}
 */
export function findPlannedStateColor(state) {
  return PLANNED_STATE_COLORS[state] ?? PLANNED_STATE_COLORS.active;
}

const PLANNED_STATE_ICON_COLORS = {
  active: "green",
  suggested: "purple",
  archived: "grey",
};

/**
 * Map a planned route's lifecycle state to its vessel-icon color key.
 * Unknown or missing state falls back to active green.
 * @param {"active"|"suggested"|"archived"|string|null|undefined} state
 * @returns {"green"|"purple"|"grey"}
 */
export function findPlannedStateIconColor(state) {
  return PLANNED_STATE_ICON_COLORS[state] ?? PLANNED_STATE_ICON_COLORS.active;
}

// Route geometry panes + z-indexes, shared by RouteLines/RoutePoints. Each route
// is a [line, points] band; bands stack reported < archived < suggested < active
// < Pathfinder, so the whole active route sits on top of suggested/archived.
export const ROUTE_PANES = {
  routeLineReportedPane: 600,
  routePointReportedPane: 605,
  routeLinePlanArchivedPane: 612,
  routePointPlanArchivedPane: 614,
  routeLinePlanSuggestedPane: 616,
  routePointPlanSuggestedPane: 618,
  routeLinePlanActivePane: 620,
  routePointPlanActivePane: 622,
  routeLinePathfinderPane: 640,
  routePointPathfinderPane: 642,
};

const PLAN_STATE_LINE_PANE = {
  active: "routeLinePlanActivePane",
  suggested: "routeLinePlanSuggestedPane",
  archived: "routeLinePlanArchivedPane",
};

const PLAN_STATE_POINT_PANE = {
  active: "routePointPlanActivePane",
  suggested: "routePointPlanSuggestedPane",
  archived: "routePointPlanArchivedPane",
};

/**
 * Resolve the Leaflet pane for a route's geometry by routeType + lifecycle
 * state, so active plan routes stack above suggested/archived ones.
 * @param {"line"|"point"} kind
 * @param {string} routeType
 * @param {"active"|"suggested"|"archived"|string|null|undefined} state
 * @returns {string}
 */
export function findRoutePane(kind, routeType, state) {
  const isPoint = kind === "point";
  if (routeType === "Pathfinder") {
    return isPoint ? "routePointPathfinderPane" : "routeLinePathfinderPane";
  }
  if (isPlanType(routeType)) {
    const map = isPoint ? PLAN_STATE_POINT_PANE : PLAN_STATE_LINE_PANE;
    return map[state] ?? map.active;
  }
  return isPoint ? "routePointReportedPane" : "routeLineReportedPane";
}

/**
 * Utilities for route styling, waypoint filtering, interpolation (rhumbline/greatcircle),
 * and popup rendering.
 */
const RouteHelper = {
  /**
   * Map a route type to a color key used by vessel icons and line styles.
   *
   * @param {"reported"|"reported_pulse"|"planned"|"planned_pulse"|"Pathfinder"|string} routeType
   * @returns {"blue"|"blue_pulse"|"green"|"green_pulse"|"yellow"|"green"} Color key string
   */
  findRouteColor(routeType) {
    const routeType2Color = {
      reported: "blue",
      reported_pulse: "blue_pulse",
      planned: "green",
      planned_pulse: "green_pulse",
      Pathfinder: "yellow",
    };
    const color = routeType2Color[routeType];
    return color ? color : "green";
  },

  /**
   * True if a route type is a planned route (ShippingDB type 'planned').
   * Delegates to the standalone isPlanType export.
   * @param {string} routeType
   * @returns {boolean}
   */
  isPlanType(routeType) {
    return isPlanType(routeType);
  },

  /**
   * Compute a rhumbline interpolated position between two points at a given speed & time.
   * Handles dateline wrapping.
   *
   * @param {GeoJSON.Point} point1 - Start point
   * @param {number} speed - Speed in km/s
   * @param {number} timeDiffInterp - Time delta in seconds
   * @param {GeoJSON.Point} point2 - End point
   * @returns {{lat:number,lng:number}} Lat/Lng of interpolated position
   */
  getRhumblinePoint(point1, speed, timeDiffInterp, point2) {
    // what about dateline crossing? problems?
    const pointOnLine = turf.rhumbDestination(
      point1,
      speed * timeDiffInterp,
      turf.rhumbBearing(point1, point2),
      {
        units: "kilometers",
      },
    );

    // Check if the longitude exceeds the valid range
    if (pointOnLine.geometry.coordinates[0] > 180) {
      pointOnLine.geometry.coordinates[0] -= 360;
    } else if (pointOnLine.geometry.coordinates[0] < -180) {
      pointOnLine.geometry.coordinates[0] += 360;
    }

    const latlonAlongRoute = {
      lat: pointOnLine.geometry.coordinates[1],
      lng: pointOnLine.geometry.coordinates[0],
    };
    return latlonAlongRoute;
  },

  /**
   * Compute a great-circle interpolated position between two points.
   * Handles dateline crossings (MultiLineString).
   *
   * @param {GeoJSON.Point} point1 - Start point
   * @param {GeoJSON.Point} point2 - End point
   * @param {number} speed - Speed in km/s
   * @param {number} timeDiffInterp - Time delta in seconds
   * @param {number} timeDiffP2P1 - Segment duration in seconds
   * @returns {{lat:number,lng:number}|undefined} Lat/Lng of interpolated position
   */
  getGreatcirclePoint(point1, point2, speed, timeDiffInterp, timeDiffP2P1) {
    const lineRoute = turf.greatCircle(point1, point2);
    // if no dateline crossing (LineString) calculate and return point along great circle route
    if (lineRoute.geometry.type === "LineString") {
      const alongRoute = turf.along(lineRoute, speed * timeDiffInterp, {
        units: "kilometers",
      });
      const latlonAlongRoute = {
        lat: alongRoute.geometry.coordinates[1],
        lng: alongRoute.geometry.coordinates[0],
      };
      return latlonAlongRoute;
    }
    // if dateline crossing (MultiLineString) then additionally check in which segment the point lies
    else if (lineRoute.geometry.type === "MultiLineString") {
      // console.log("greatCirclePoint", point1.geometry.coordinates, point2.geometry.coordinates, lineRoute)
      // const travelDistance = speed * timeDiffP2P1 // distance the vessel has traveled
      const travelDistance = turf.length(lineRoute, { units: "kilometers" });
      let accumulatedDistance = 0;
      for (let i = 0; i < lineRoute.geometry.coordinates.length; i++) {
        const segment = turf.lineString(lineRoute.geometry.coordinates[i]);
        const segmentDistance = turf.length(segment, { units: "kilometers" });
        // console.log(
        //   "mulitLinestring",
        //   travelDistance,
        //   accumulatedDistance,
        //   segmentDistance,
        //   accumulatedDistance + segmentDistance,
        //   accumulatedDistance + segmentDistance > travelDistance
        // )
        if (accumulatedDistance + segmentDistance > travelDistance) {
          // This is the segment on which the point lies
          const residualDistance = travelDistance - accumulatedDistance;
          const alongSegment = turf.along(segment, residualDistance, {
            units: "kilometers",
          });
          // console.log("alongSegment", alongSegment)
          return {
            lat: alongSegment.geometry.coordinates[1],
            lng: alongSegment.geometry.coordinates[0],
          };
        } else if (accumulatedDistance + segmentDistance === travelDistance) {
          return {
            lat: point2.geometry.coordinates[1],
            lng: point2.geometry.coordinates[0],
          };
        } else {
          accumulatedDistance += segmentDistance;
        }
      }
    }
  },

  /**
   * Compute speed and interpolation time differences for two points.
   *
   * @param {GeoJSON.Point} point1
   * @param {GeoJSON.Point} point2
   * @param {string|number|Date} timestamp1
   * @param {string|number|Date} timestamp2
   * @param {string|number|Date} label - Target time
   * @returns {{speed:number,timeDiffInterp:number}} km/s and seconds since last point
   */
  calculateDynamics(point1, point2, timestamp1, timestamp2, label) {
    const distance = turf.distance(point1, point2, { units: "kilometers" });
    const timeDiffInterp = Math.abs(
      (dayjs.utc(timestamp1) - dayjs.utc(label)) / 1000,
    );
    const timeDiffP2P1 = (dayjs.utc(timestamp2) - dayjs.utc(timestamp1)) / 1000;
    const speed = distance / timeDiffP2P1;

    return { speed, timeDiffInterp };
  },

  /**
   * Dispatch interpolation to rhumbline or great-circle based on legtype.
   *
   * @param {"rhumbline"|"greatcircle"} legtype
   * @param {GeoJSON.Point} point1
   * @param {number} speed
   * @param {number} timeDiffInterp
   * @param {GeoJSON.Point} point2
   * @returns {{lat:number,lng:number}}
   */
  determineLegTypeAndCallFunction(
    legtype,
    point1,
    speed,
    timeDiffInterp,
    point2,
  ) {
    let latlonAlongRoute;

    if (legtype === "rhumbline") {
      latlonAlongRoute = RouteHelper.getRhumblinePoint(
        point1,
        speed,
        timeDiffInterp,
        point2,
      );
    } else {
      latlonAlongRoute = RouteHelper.getGreatcirclePoint(
        point1,
        point2,
        speed,
        timeDiffInterp,
      );
    }

    return latlonAlongRoute;
  },

  /**
   * Filter visible waypoints based on current viewport bounds and zoom level.
   *
   * @param {{map:import("leaflet").Map,features:Array<GeoJSON.Feature>,zoomLevel:number,routeType:string}} params
   * @returns {Array<GeoJSON.Feature>|null} Filtered waypoints or null if none
   */
  filterViewportBoundingbox({
    map,
    features: waypoints,
    zoomLevel,
    routeType,
  }) {
    if (routeType === "reported") {
      // when only one feature available return this
      if (
        waypoints.length === 1 &&
        map
          .getBounds()
          .contains([
            waypoints[0].geometry.coordinates[1],
            waypoints[0].geometry.coordinates[0],
          ])
      ) {
        return waypoints;
      }
      for (let i = 0; i < waypoints.length - 1; i++) {
        let filteredWaypoints = null;
        if (
          map
            .getBounds()
            .contains([
              waypoints[i].geometry.coordinates[1],
              waypoints[i].geometry.coordinates[0],
            ])
        ) {
          filteredWaypoints = RouteHelper.filterWps(waypoints, zoomLevel);
          // console.log("filteredViewport", filteredWaypoints)
          return filteredWaypoints;
        } else if (filteredWaypoints === null) {
          // filteredWaypoints = waypoints
          // console.log("no points in filterViewportBoundingbox")
          // return filteredWaypoints
        }
      }
    } else if (routeType === "planned") {
      // hier muss eventuell noch eine andere Lösung her, falls viele Plan Routen/Punkte vorhanden
      const filteredWaypoints = waypoints;
      // console.log("filterViewportBoundingbox plan", filteredFeatures)
      return filteredWaypoints;
    }
    const result = null;
    return result;
  },

  /**
   * Thin waypoints based on zoom level.
   * Always keeps the first and last feature.
   *
   * @param {Array<GeoJSON.Feature>} features
   * @param {number} zoomLevel
   * @returns {Array<GeoJSON.Feature>}
   */
  filterWps(features, zoomLevel) {
    return features.filter((feature, j) => {
      // check if both coordinates are defined
      if (
        feature.geometry.coordinates &&
        feature.geometry.coordinates[0] &&
        feature.geometry.coordinates[1]
      ) {
        // Include the first and last feature
        if (j === 1 || j === features.length - 1) {
          return true;
        } else {
          return j % zoomWpNumberThreshold[Math.floor(zoomLevel)] === 0;
        }
      }
    });
  },

  /**
   * Calculate current interpolated position along a PLAN route relative to now.
   *
   * @param {{geojson:GeoJSON.FeatureCollection}} route
   * @returns {{lat:number,lng:number}|undefined}
   */
  getCurrentPlanPosition(route) {
    let point1 = null;
    let point2 = null;
    for (let i = 1; i < route.geojson.features.length; i++) {
      if (
        dayjs
          .utc(route.geojson.features[i].properties.timestamp)
          .isSameOrAfter(nowUtc())
      ) {
        point1 = turf.point(route.geojson.features[i - 1].geometry.coordinates);
        point2 = turf.point(route.geojson.features[i].geometry.coordinates);
        const distance = turf.distance(point1, point2, { units: "kilometers" });
        const timeDiffP2P1 =
          (dayjs.utc(route.geojson.features[i].properties.timestamp) -
            dayjs.utc(route.geojson.features[i - 1].properties.timestamp)) /
          1000;
        const timeDiffInterp =
          Math.abs(
            nowUtc() -
              dayjs.utc(route.geojson.features[i - 1].properties.timestamp),
          ) / 1000;
        const speed = distance / timeDiffP2P1;
        if (route.geojson.features[i - 1].properties.legtype === "rhumbline") {
          const latlonAlongRoute = RouteHelper.getRhumblinePoint(
            point1,
            speed,
            timeDiffInterp,
            point2,
          );
          // console.log("currentPosition rhumbline", latlonAlongRoute)
          return latlonAlongRoute;
        } else {
          const latlonAlongRoute = RouteHelper.getGreatcirclePoint(
            point1,
            point2,
            speed,
            timeDiffInterp,
          );
          // console.log("currentPosition great circle", latlonAlongRoute)
          return latlonAlongRoute;
        }
      }
    }
  },

  /**
   * Check whether at least one plan route exists for the marked vessels.
   *
   * @param {Array<{vesselName:string}>} markedVesselNames
   * @param {Array<{points:{vesselName:string,geojson:GeoJSON.FeatureCollection}}>} vesselPlanRoutes
   * @returns {boolean}
   */
  planRouteExists(markedVesselNames, vesselPlanRoutesOrWrapper) {
    const vesselPlanRoutes = Array.isArray(vesselPlanRoutesOrWrapper)
      ? vesselPlanRoutesOrWrapper
      : (vesselPlanRoutesOrWrapper?.meteotrackerWaypointsGeojson?.nodes ?? []);

    if (!Array.isArray(vesselPlanRoutes)) return false;
    if (!Array.isArray(markedVesselNames) || markedVesselNames.length === 0)
      return true;

    for (const marked of markedVesselNames) {
      for (const route of vesselPlanRoutes) {
        const name = route?.points?.vesselName ?? route?.vesselName;
        const features =
          route?.points?.geojson?.features ?? route?.geojson?.features ?? [];
        if (marked?.vesselName === name && features.length > 0) {
          return true;
        }
      }
    }
    return false;
  },

  /**
   * Check whether at least one REPORTED route exists for the marked vessels.
   *
   * @param {Array<{vesselName:string}>} markedVesselNames
   * @param {Array|Object} vesselReportedRoutesOrWrapper
   * @returns {boolean}
   */
  reportedRouteExists(markedVesselNames, vesselReportedRoutesOrWrapper) {
    const vesselReportedRoutes = Array.isArray(vesselReportedRoutesOrWrapper)
      ? vesselReportedRoutesOrWrapper
      : (vesselReportedRoutesOrWrapper?.meteotrackerReportedWaypointsGeojson
          ?.nodes ?? []);

    if (!Array.isArray(vesselReportedRoutes)) return false;
    // Keep switch enabled when no vessel is explicitly marked (same UX as planRouteExists)
    if (!Array.isArray(markedVesselNames) || markedVesselNames.length === 0)
      return true;

    for (const marked of markedVesselNames) {
      for (const route of vesselReportedRoutes) {
        const name = route?.points?.vesselName ?? route?.vesselName;
        const features =
          route?.points?.geojson?.features ?? route?.geojson?.features ?? [];
        if (marked?.vesselName === name && features.length > 0) {
          return true;
        }
      }
    }
    return false;
  },
};

export default RouteHelper;
